/* eslint-disable ember/classic-decorator-hooks, ember/no-runloop */
import Service from '@ember/service';
import { cancel, later } from '@ember/runloop';

// layout is a pub/sub bus for layout changes that affect component dimensions
// (sidebar open/close, hub submenu expand, isWideList toggling, window resize, etc.).
// Components that need to re-measure on layout changes register a callback;
// layout-changing components call notifyLayoutChange() to trigger them.
// Window resize is handled here centrally so components only need this service.
export default class LayoutControlsService extends Service {
  _listeners = null;
  _boundOnWindowResize = null;
  _pendingNotify = null;

  init() {
    super.init(...arguments);
    this._listeners = new Set();
    this._boundOnWindowResize = () => this._notify();
    window.addEventListener('resize', this._boundOnWindowResize);
  }

  setup() {
    this._onKeyDown = this._handleMainContentKeyDown.bind(this);
    document.addEventListener('keydown', this._onKeyDown);
  }

  teardown() {
    document.removeEventListener('keydown', this._onKeyDown);
  }

  // addListener registers a callback to invoke when layout changes.
  addListener(callback) {
    this._listeners.add(callback);
  }

  // removeListener deregisters a previously added callback.
  removeListener(callback) {
    this._listeners.delete(callback);
  }

  // notifyLayoutChange triggers all listeners after a delay to allow CSS
  // transitions (e.g. sidenav slide animation) to complete before listeners
  // re-measure DOM dimensions. Callers can pass a custom delay in ms.
  notifyLayoutChange(delay = 300) {
    cancel(this._pendingNotify);
    this._pendingNotify = later(this, this._notify, delay);
  }

  _notify() {
    this._listeners.forEach((cb) => cb());
  }

  _handleMainContentKeyDown(e) {
    const SCROLL_KEYS = {
      ArrowUp: -100,
      ArrowDown: 100,
      ArrowLeft: -100,
      ArrowRight: 100,
    };
    if (!(e.key in SCROLL_KEYS)) return;

    let active = document.activeElement;
    let tag = active?.tagName.toLowerCase();
    let editable = active?.isContentEditable;
    const EXCLUDED_TAGS = new Set(['input', 'textarea', 'select']);
    const insideMdComponent = (el) => {
      for (
        let node = el;
        node && node !== document.body;
        node = node.parentElement
      ) {
        if (node.tagName?.toLowerCase().startsWith('md-')) return true;
      }
      return false;
    };
    if (editable || EXCLUDED_TAGS.has(tag) || insideMdComponent(active)) return;

    let container = document.querySelector('.main-content__container');
    if (!container) return;

    let isVertical = e.key === 'ArrowUp' || e.key === 'ArrowDown';
    if (isVertical) {
      if (e.metaKey || e.ctrlKey) {
        container.scrollTop =
          e.key === 'ArrowDown' ? container.scrollHeight : 0;
      } else {
        container.scrollTop += SCROLL_KEYS[e.key];
      }
      if (document.activeElement !== container) {
        container.focus({ preventScroll: true });
      }
    } else {
      if (e.metaKey || e.ctrlKey) {
        return;
      }
      container.scrollLeft += SCROLL_KEYS[e.key];
    }

    e.preventDefault();
  }

  willDestroy() {
    cancel(this._pendingNotify);
    window.removeEventListener('resize', this._boundOnWindowResize);
    this._listeners.clear();
    super.willDestroy(...arguments);
  }
}
